import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import { Box, Button, DialogContent, Divider, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { computeIntraCommunityVatFR } from '@kheops/utils';
import useResolvedAdditionalAddress from '../address/hook/useResolvedAdditionalAddress';
import useFormattedAddress from '../address/hook/useFormattedAddress';
import { BuyerInfoViewProps } from './BuyerInfoDialog';

export default function BuyerInfoInvoicingAddressView({ company, close, changeView }: BuyerInfoViewProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts', 'settings']);
  const invoicingAddress = useResolvedAdditionalAddress({ companyName: company.legalName, companyAddress: company.addresses[0], additionalAddress: company.invoicing_address });
  const formattedAddress = useFormattedAddress(invoicingAddress);

  return (
    <DialogContent
      sx={{
        maxWidth: 500,
        height: 620,
        p: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={() => changeView('INVOICING')}
          >
            <ArrowBackIcon />
          </Button>
          <PlaceIcon sx={{ mx: 1 }} />
          <Typography variant="titleMedium" sx={{ flex: 1 }}>
            {t('contracts:invoicing_address')}
          </Typography>
          <Button variant="text" onClick={close}>
            <CloseIcon />
          </Button>
        </Box>
        <Typography variant="bodySmall" color="secondary">
          {t('contracts:buyer_invoicing_information')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          px: 1,
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="titleMedium">
            📍 {t('common:address')}
          </Typography>
          <Typography
            variant="bodyMedium"
            sx={{
              whiteSpace: 'pre-line',
              pl: 1,
            }}
          >
            {formattedAddress}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="titleMedium">
            ✍️ {t('settings:additional_info')}
          </Typography>
          <Typography
            variant="bodyMedium"
            sx={{
              whiteSpace: 'pre-line',
              pl: 1,
            }}
          >
            {company.invoicing_instructions || '-'}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="titleSmall">
            {t('common:national_company_registration_number')}
          </Typography>
          <Typography variant="bodyMedium">
            {company.national_company_registration_number}
          </Typography>
          <Typography variant="titleSmall">
            {t('common:vat_number')}
          </Typography>
          <Typography variant="bodyMedium">
            {computeIntraCommunityVatFR(company.national_company_registration_number)}
          </Typography>
        </Box>
      </Box>
    </DialogContent>
  );
}
